import { injectSpeedInsights } from "@vercel/speed-insights/sveltekit";
import { type Session, session } from "app/stores/auth";
import { get } from "svelte/store";
import { browser } from "$app/environment";
import posthog from "posthog-js";
import {
  getSubdomainInfo,
  healthcheckDiagnoseUrl,
  sveltekitApiUrl,
  verifyEmailUrl,
} from "$utils/urls";
import { goto } from "$app/navigation";
import { redirect } from "@sveltejs/kit";
import { VERCEL } from "api";
import type { LayoutLoad } from "./$types";
import { PUBLIC_POSTHOG_PROJECT_ID } from "$env/static/public";

injectSpeedInsights();

export const ssr = true;

export const load: LayoutLoad = async ({ route, url, fetch, data: serverData }) => {
  let auth: Session | undefined;
  const { vercel_env } = VERCEL;

  // if running as a preview app, check if the backend is up
  if (vercel_env === "preview") {
    const response = await fetch(`${sveltekitApiUrl()}/diagnose`);
    const data = await response.json();

    // if backend is down and we aren't already on the healthcheck page, redirect to it
    // this route check is needed to avoid too many redirects
    if (!data.isUp && route.id !== "/healthcheck/diagnose") {
      redirect(307, healthcheckDiagnoseUrl(false, url));
    }
  }

  if (browser) {
    // init auth client-side
    auth = get(session);
    if (!auth.inited) {
      // if something needs to wait on this promise, it can await on auth.promise
      session.init().then(async () => {
        auth = get(session);

        //force users to verify email before allowing access
        if (
          !auth.insideAuthFlow &&
          auth.firebaseUser &&
          !auth.firebaseUser?.emailVerified &&
          route.id !== "/(user)/verify-email" &&
          route.id !== "/auth" &&
          route.id !== "/auth/verify-email"
        ) {
          goto(`${verifyEmailUrl(false, url)}/?next=${url}`);
        }
      });
    }
    if (PUBLIC_POSTHOG_PROJECT_ID) {
      // load posthog as early as possible
      posthog.init(PUBLIC_POSTHOG_PROJECT_ID, {
        api_host: "https://app.outverse.com/ingest",
        ui_host: "https://us.i.posthog.com",
        // don't double count (instrumented via after/before navigate instead)
        capture_pageview: false,
        capture_pageleave: false,
        session_recording: {
          maskAllInputs: true,
          maskInputOptions: {
            textarea: false,
          },
        },
      });
    }
  }

  const { usingSubdomain, pathPrefix } = getSubdomainInfo(url.hostname);
  return {
    og: {
      title: "Outverse",
      description: "The self-serve support platform for modern SaaS.",
      url: "https://outverse.com",
    },
    userData: serverData.userData,
    auth,
    usingSubdomain,
    pathPrefix,
  };
};
